// src/components/goal-settings/KpiCascadeModal.jsx
import React, { useEffect, useMemo, useState } from 'react';

/**
 * Props:
 * - show
 * - kpaId
 * - onClose()
 * - onSubmit({ kpi_ids })
 * - fetchManagerKpis: (kpaId) => { manager?: {name,email}, kpis: [] }
 */
export default function KpiCascadeModal({
                                            show,
                                            kpaId,
                                            onClose,
                                            onSubmit,
                                            fetchManagerKpis,
                                        }) {
    const isOpen = !!show;

    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState('');
    const [manager, setManager] = useState(null);
    const [rows, setRows] = useState([]);
    const [selected, setSelected] = useState([]);

    useEffect(() => {
        if (!isOpen || !kpaId) return;
        (async () => {
            setLoading(true); setErr('');
            try {
                const { manager: m, kpis } = await fetchManagerKpis(kpaId);
                setManager(m || null);
                setRows(Array.isArray(kpis) ? kpis : []);
            } catch (e) {
                setErr(e?.response?.data?.message || 'Failed to load manager KPIs.');
            } finally {
                setLoading(false);
            }
        })();
    }, [isOpen, kpaId, fetchManagerKpis]);

    const toggle = (id) => {
        setSelected(prev => {
            const s = new Set(prev);
            const key = String(id);
            s.has(key) ? s.delete(key) : s.add(key);
            return Array.from(s);
        });
    };
    const selectAll = () => setSelected(rows.map(r => String(r.id)));
    const clearAll = () => setSelected([]);

    const fmt = (r, f) => {
        const v = r?.[f];
        if (v == null || v === '') return '—';
        if (r?.is_percentage) return `${v}%`;
        return `${v}${r?.unit ? ` ${r.unit}` : ''}`;
    };

    const submit = async () => {
        await onSubmit?.({ kpi_ids: selected.map(Number) });
    };

    if (!isOpen) return null;

    return (
        <div className="modal show" style={{ display: 'block', background: 'rgba(0,0,0,.4)' }} aria-modal="true" role="dialog">
            <div className="modal-dialog modal-lg modal-dialog-scrollable">
                <div className="modal-content">
                    <div className="modal-header py-2">
                        <h6 className="modal-title">Cascade KPIs</h6>
                        <button type="button" className="btn-close" onClick={onClose} />
                    </div>

                    <div className="modal-body">
                        {err && <div className="alert alert-danger py-1">{err}</div>}

                        <div className="d-flex align-items-center justify-content-between mb-2">
                            <div>
                                <div className="fw-semibold">Manager</div>
                                <div className="text-muted small">
                                    {manager ? (<>{manager.name} · {manager.email}</>) : '—'}
                                </div>
                            </div>
                        </div>

                        {loading && <div className="text-muted">Loading manager KPIs…</div>}

                        {!loading && rows.length === 0 && (
                            <div className="alert alert-info py-1">No manager KPIs found (aligned KPA not found or empty).</div>
                        )}

                        {!loading && rows.length > 0 && (
                            <>
                                <div className="d-flex gap-2 mb-2">
                                    <button className="btn btn-sm btn-outline-secondary" onClick={selectAll}>Select all</button>
                                    <button className="btn btn-sm btn-outline-secondary" onClick={clearAll}>Clear</button>
                                </div>

                                <table className="table table-sm align-middle">
                                    <thead>
                                    <tr>
                                        <th style={{width: 32}}></th>
                                        <th>Name</th>
                                        <th style={{width: 120, textAlign:'right'}}>Threshold</th>
                                        <th style={{width: 120, textAlign:'right'}}>Target</th>
                                        <th style={{width: 120, textAlign:'right'}}>Stretch</th>
                                        <th style={{width: 100, textAlign:'right'}}>Weight</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {rows.map(r => {
                                        const checked = selected.includes(String(r.id));
                                        return (
                                            <tr key={r.id}>
                                                <td>
                                                    <input type="checkbox" className="form-check-input" checked={checked} onChange={() => toggle(r.id)} />
                                                </td>
                                                <td>{r.name}</td>
                                                <td style={{textAlign:'right'}}>{fmt(r, 'threshold')}</td>
                                                <td style={{textAlign:'right'}}>{fmt(r, 'target')}</td>
                                                <td style={{textAlign:'right'}}>{fmt(r, 'stretch')}</td>
                                                <td style={{textAlign:'right'}}>{r.weight ?? '—'}</td>
                                            </tr>
                                        );
                                    })}
                                    </tbody>
                                </table>
                            </>
                        )}
                    </div>

                    <div className="modal-footer py-2">
                        <button className="btn btn-sm btn-light" onClick={onClose}>Close</button>
                        <button className="btn btn-sm btn-primary" onClick={submit} disabled={!selected.length}>
                            Cascade Selected
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}